import {
  Box,
  FormControlProps,
  IconButton,
  styled,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { FocusEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { DynamicIcon, Tooltip } from 'src/components';
import { FormTextField } from 'src/components/Forms/FormTextField/form-text-field';
import { CommonFormFieldProps } from '..';

export type FormEditableTextProps = Omit<
  FormControlProps,
  'required' | 'defaultValue' | 'name' | 'type'
> & {
  label?: string;
  disabled?: boolean;
  required?: boolean | string;
  textFieldProps?: Partial<TextFieldProps>;
  value?: string;
  disableClickHandlers?: boolean;
  isEditing?: boolean;
} & CommonFormFieldProps<'editable-text', string>;

const StyledIconButton = styled(IconButton, {
  name: 'FormEditableTextIconBtn',
})(({ theme }) => ({
  padding: theme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
  marginLeft: theme.spacing(0.5),

  '& .material-symbols-outlined': {
    width: '1rem',
    height: '1rem',
    fontSize: '1rem',
  },
}));

export const FormEditableText = (props: FormEditableTextProps) => {
  const {
    disabled = false,
    textFieldProps = {},
    onBlur,
    disableClickHandlers,
    isEditing: incomingIsEditing = false,
    ...rest
  } = props;

  const [isEditing, setIsEditing] = useState(incomingIsEditing);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // triggering edit state from outside
    setIsEditing(incomingIsEditing);
  }, [incomingIsEditing]);

  const { getValues, resetField } = useFormContext();

  const value = getValues(props.name) as string;

  const handleClick = useCallback(() => {
    if (!disabled && !disableClickHandlers) {
      setIsEditing(true);
    }
  }, [disabled, disableClickHandlers]);

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (!event.target.value) {
        resetField(props.name);
      }

      setIsEditing(false);
      onBlur?.(event);
    },
    [onBlur, props.name, resetField],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        inputRef.current?.blur();
      }
    },
    [inputRef],
  );

  return isEditing ? (
    <FormTextField
      onBlur={handleBlur}
      textFieldProps={{
        inputRef,
        autoFocus: true,
        onKeyDown: handleKeyDown,
        ...textFieldProps,
      }}
      sx={{
        '&.MuiFormControl-root': {
          width: '100%',
        },
      }}
      {...rest}
      label=""
      type="textfield"
    />
  ) : (
    <Box display="flex" alignItems="center" maxWidth="100%" minWidth={0}>
      <Typography
        onClick={handleClick}
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          cursor: disabled ? 'auto' : 'pointer',
        }}
        variant="subtitle2"
      >
        {value}
      </Typography>

      {!disabled && !disableClickHandlers && (
        <Tooltip title="Edit">
          <StyledIconButton onClick={handleClick}>
            <DynamicIcon name="edit" />
          </StyledIconButton>
        </Tooltip>
      )}
    </Box>
  );
};
